import { useEffect, useState } from "react";
import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import SortHeader from "./SortHeader";
import {
  EventsApi,
  type EventRow,
  type EventSortField,
  type Pricing,
} from "../api/events";
import { centsToUsd, usdToCents } from "../lib/money";
import { readSort, writeSort, SORT_KEYS, type SortDir } from "../lib/tableSort";
import EventEditModal from "./EventEditModal";
import logger from "../analytics/logger";

// Event dates are calendar dates (no meaningful time-of-day) stored in a
// timestamptz column, so the API returns e.g. "2027-03-04T00:00:00+00:00".
// Parsing that with `new Date(...)` shifts it to the viewer's local zone and can
// roll back a day (UTC midnight → previous evening in the Americas). Format from
// the date part only — matching how the edit modal (toDateInput) shows it.
function fmtDateOnly(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "—";
  return new Date(y, m - 1, d).toLocaleDateString();
}

function effectivePricing(e: EventRow): Pricing {
  return (
    e.effective_pricing ?? {
      creator_verification: null,
      work_certification: null,
      free_works: null,
    }
  );
}

export default function OriginEventsList({ originId }: { originId: string; originSlug?: string }) {
  const [rows, setRows] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Coerce any remembered sort that names a column no longer shown (e.g. the
  // removed "created_at" column) to the new default, Start date descending.
  const initial = (() => {
    const remembered = readSort(SORT_KEYS.originEvents);
    const valid: EventSortField[] = ["event_date", "event_end_date", "name"];
    return valid.includes(remembered.sort as EventSortField)
      ? { sort: remembered.sort as EventSortField, dir: remembered.dir }
      : { sort: "event_date" as EventSortField, dir: "desc" as SortDir };
  })();
  const [sort, setSort] = useState<EventSortField>(initial.sort);
  const [dir, setDir] = useState<SortDir>(initial.dir);
  const [editEvent, setEditEvent] = useState<EventRow | null>(null);
  const toast = useToast();

  const load = () => {
    setLoading(true);
    EventsApi.list({ origin_id: originId, page: 1, page_size: 200, sort, dir })
      .then((r) => setRows(r.events))
      .catch((e) => {
        logger.error("origin_events_list_failed", e);
        toast({ status: "error", title: "Failed to load events", duration: 3000 });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void (async () => {
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originId, sort, dir]);

  const toggleSort = (field: EventSortField) => {
    let nextDir: SortDir;
    const nextSort: EventSortField = field;
    if (sort === field) {
      nextDir = dir === "asc" ? "desc" : "asc";
    } else {
      nextDir = field === "name" ? "asc" : "desc";
    }
    setSort(nextSort);
    setDir(nextDir);
    writeSort(SORT_KEYS.originEvents, { sort: nextSort, dir: nextDir });
  };

  const shProps = (field: EventSortField) => ({
    active: sort === field,
    dir,
    onToggle: () => toggleSort(field),
  });

  const addEvent = async () => {
    try {
      const created = await EventsApi.create({
        origin_id: originId,
        name: "New event",
        event_date: null,
        location: null,
      });
      load();
      // best-effort focus after refetch
      setTimeout(() => {
        const el = document.querySelector<HTMLInputElement>(`input[data-event-name="${created.id}"]`);
        el?.focus();
      }, 200);
    } catch (e) {
      logger.error("origin_event_create_failed", e);
      toast({ status: "error", title: "Failed to add event", duration: 3000 });
    }
  };

  const deleteEvent = async (id: string) => {
    try {
      const res = await EventsApi.bulkDelete([id]);
      if (res.refused.includes(id)) {
        toast({ status: "warning", title: "Event is referenced by a creator", duration: 4000 });
        return;
      }
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      logger.error("origin_event_delete_failed", e);
      toast({ status: "error", title: "Failed to delete event", duration: 3000 });
    }
  };

  const saveName = async (row: EventRow, value: string) => {
    if ((row.name ?? "") === value) return;
    try {
      await EventsApi.update(row.id, { name: value || null });
      toast({ status: "success", title: "Saved", duration: 1500 });
      load();
    } catch (e) {
      logger.error("origin_event_update_failed", e);
      toast({ status: "error", title: "Failed to save", duration: 3000 });
    }
  };

  const savePricing = async (
    row: EventRow,
    field: "creator_verification" | "work_certification" | "free_works",
    value: number | null
  ) => {
    // Change-guard: onBlur fires even when nothing was edited. Without this,
    // tabbing through an inherited row's price field would persist a spurious
    // override (base=eff → all inherited values copied, this field nulled).
    const currentStored = row.pricing ? row.pricing[field] ?? null : null;
    if (currentStored === value) return;

    const eff = effectivePricing(row);
    const current = row.pricing;
    const base = current ?? eff;
    const next: Pricing = { ...base, [field]: value };
    if (
      next.creator_verification == null &&
      next.work_certification == null &&
      next.free_works == null
    ) {
      try {
        await EventsApi.update(row.id, { pricing: null });
        toast({ status: "success", title: "Saved", duration: 1500 });
        load();
      } catch (e) {
        logger.error("origin_event_update_failed", e);
        toast({ status: "error", title: "Failed to save", duration: 3000 });
      }
      return;
    }
    try {
      await EventsApi.update(row.id, { pricing: next });
      toast({ status: "success", title: "Saved", duration: 1500 });
      load();
    } catch (e) {
      logger.error("origin_event_update_failed", e);
      toast({ status: "error", title: "Failed to save", duration: 3000 });
    }
  };

  return (
    <Box>
      <HStack justify="flex-end" mb={2}>
        <Button size="sm" leftIcon={<AddIcon boxSize={2.5} />} onClick={addEvent}>
          Add event
        </Button>
      </HStack>
      {loading ? (
        <Box py={6} textAlign="center">
          <Spinner size="sm" />
        </Box>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderRadius="md">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <SortHeader label="Name" {...shProps("name")} />
                <Th>Creator verification</Th>
                <Th>Work certification</Th>
                <Th>Free works</Th>
                <SortHeader label="Start" {...shProps("event_date")} />
                <SortHeader label="End" {...shProps("event_end_date")} />
                <Th width="1%" />
              </Tr>
            </Thead>
            <Tbody>
              {rows.length === 0 ? (
                <Tr>
                  <Td colSpan={7}>
                    <Text color="gray.500" fontSize="sm" py={2}>
                      No events yet.
                    </Text>
                  </Td>
                </Tr>
              ) : (
                rows.map((row) => (
                  <EventRowEditor
                    key={row.id}
                    row={row}
                    onSaveName={(v) => saveName(row, v)}
                    onSavePricing={(f, v) => savePricing(row, f, v)}
                    onEdit={() => setEditEvent(row)}
                    onDelete={() => deleteEvent(row.id)}
                  />
                ))
              )}
            </Tbody>
          </Table>
        </Box>
      )}
      <EventEditModal
        event={editEvent}
        isOpen={!!editEvent}
        onClose={() => setEditEvent(null)}
        onSaved={() => {
          setEditEvent(null);
          load();
        }}
      />
    </Box>
  );
}

function EventRowEditor({
  row,
  onSaveName,
  onSavePricing,
  onEdit,
  onDelete,
}: {
  row: EventRow;
  onSaveName: (v: string) => void;
  onSavePricing: (
    field: "creator_verification" | "work_certification" | "free_works",
    value: number | null
  ) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const eff = effectivePricing(row);
  const inherited = row.pricing_inherited || row.pricing == null;
  // The origin's baseline event is identified purely by this name, so it must
  // not be renamed or deleted here (that would orphan the baseline). Its
  // pricing and edit/QR modal stay available — surfacing it is the point.
  const isDefault = row.name === "Default";

  const [prevRow, setPrevRow] = useState(row);
  const [name, setName] = useState(row.name ?? "");
  const [cv, setCv] = useState(row.pricing?.creator_verification != null ? centsToUsd(row.pricing.creator_verification) : "");
  const [wc, setWc] = useState(row.pricing?.work_certification != null ? centsToUsd(row.pricing.work_certification) : "");
  const [fw, setFw] = useState(row.pricing?.free_works != null ? String(row.pricing.free_works) : "");

  // Re-seed the editable fields when the underlying row changes (e.g. after a
  // save + refetch). Adjusting state during render is React's sanctioned
  // alternative to a syncing effect (and avoids set-state-in-effect).
  if (row !== prevRow) {
    setPrevRow(row);
    setName(row.name ?? "");
    setCv(row.pricing?.creator_verification != null ? centsToUsd(row.pricing.creator_verification) : "");
    setWc(row.pricing?.work_certification != null ? centsToUsd(row.pricing.work_certification) : "");
    setFw(row.pricing?.free_works != null ? String(row.pricing.free_works) : "");
  }

  const certFree = eff.work_certification === 0;

  return (
    <Tr>
      <Td>
        {isDefault ? (
          <Text fontSize="sm" fontWeight="medium" color="gray.600">
            {row.name} <Text as="span" color="gray.400">(baseline)</Text>
          </Text>
        ) : (
          <Input
            size="sm"
            data-event-name={row.id}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => onSaveName(name)}
          />
        )}
      </Td>
      <Td>
        <InputGroup size="sm">
          <InputLeftAddon>$</InputLeftAddon>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={cv}
            placeholder={inherited ? centsToUsd(eff.creator_verification) : undefined}
            onChange={(e) => setCv(e.target.value)}
            onBlur={() => onSavePricing("creator_verification", usdToCents(cv))}
          />
        </InputGroup>
      </Td>
      <Td>
        <InputGroup size="sm">
          <InputLeftAddon>$</InputLeftAddon>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={wc}
            placeholder={inherited ? centsToUsd(eff.work_certification) : undefined}
            onChange={(e) => setWc(e.target.value)}
            onBlur={() => onSavePricing("work_certification", usdToCents(wc))}
          />
        </InputGroup>
      </Td>
      <Td>
        {certFree ? (
          <Text color="gray.500" fontSize="sm">
            Unlimited
          </Text>
        ) : (
          <Input
            size="sm"
            type="number"
            min={0}
            value={fw}
            placeholder={inherited && eff.free_works != null ? String(eff.free_works) : undefined}
            onChange={(e) => setFw(e.target.value)}
            onBlur={() => {
              const t = fw.trim();
              const n = t === "" ? null : Math.max(0, Math.trunc(Number(t)));
              onSavePricing("free_works", Number.isNaN(n as number) ? null : n);
            }}
          />
        )}
      </Td>
      <Td whiteSpace="nowrap" fontSize="sm" color="gray.600">
        {fmtDateOnly(row.event_date)}
      </Td>
      <Td whiteSpace="nowrap" fontSize="sm" color="gray.600">
        {fmtDateOnly(row.event_end_date)}
      </Td>
      <Td>
        <HStack spacing={1}>
          <IconButton
            aria-label="Edit event"
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            onClick={onEdit}
          />
          {!isDefault && (
            <IconButton
              aria-label="Delete event"
              icon={<DeleteIcon />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={onDelete}
            />
          )}
        </HStack>
      </Td>
    </Tr>
  );
}
